import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, MessageSquare, Reply, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { eur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ChatSegnalazione } from "@/components/ChatSegnalazione";

const quando = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** Campanella in barra: segnalazioni pratiche e messaggi della chat temporanea per pratica. */
export function CampanellaNotifiche() {
  const { user } = useAuth();
  const [dati, setDati] = useState({ rows: [], non_lette: 0 });
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState(null); // { notificaId, chatId }
  const ref = useRef(null);
  const navigate = useNavigate();

  const carica = () => api.get("/notifiche?limite=30").then((r) => setDati(r.data)).catch(() => {});
  useEffect(() => {
    carica();
    const id = setInterval(carica, 60_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const chiudi = (e) => {
      if (e.target.closest?.('[data-testid="notifiche-pannello"]')) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", chiudi);
    return () => document.removeEventListener("mousedown", chiudi);
  }, [open]);

  const apri = async (n) => {
    if (!n.letta) await api.post(`/notifiche/${n.id}/letta`).catch(() => {});
    setOpen(false);
    carica();
    navigate(`/pratiche?search=${encodeURIComponent(n.external_id || "")}`);
  };
  const tutteLette = async () => { await api.post("/notifiche/tutte-lette").catch(() => {}); carica(); };
  const apriChat = async (n) => {
    if (chat?.notificaId === n.id) { setChat(null); return; }
    try {
      const chatId = n.chat_id || (await api.post(`/notifiche/${n.id}/chat`)).data.chat_id;
      setChat({ notificaId: n.id, chatId });
    } catch (e) { toast.error(apiError(e)); }
  };

  const pannello = (
        <div data-testid="notifiche-pannello"
          className="fixed inset-0 flex flex-col bg-white z-[3000] sm:rounded-xl sm:border sm:border-slate-200 sm:shadow-2xl sm:z-[1200]
            sm:absolute sm:inset-auto sm:right-0 sm:mt-2 sm:w-[400px] sm:max-w-[92vw] sm:block">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500">Notifiche</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setOpen(false)} data-testid="notifiche-chiudi" aria-label="Chiudi notifiche"
                className="sm:hidden inline-flex items-center text-xs text-slate-500 hover:text-[#0018A8]"><X size={16} /></button>
              <button type="button" onClick={() => { setOpen(false); navigate("/storico-chat"); }} data-testid="notifiche-storico-chat"
                className="inline-flex items-center gap-1 text-xs text-[#0018A8] hover:underline">
                <MessageSquare size={14} /> Storico chat
              </button>
              {dati.non_lette > 0 && (
                <button type="button" onClick={tutteLette} data-testid="notifiche-tutte-lette"
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0018A8] transition-colors duration-200">
                  <CheckCheck size={14} /> Segna tutte lette
                </button>
              )}
            </div>
          </div>
          <ul className="flex-1 min-h-0 overflow-y-auto overscroll-contain divide-y divide-slate-100 sm:flex-none sm:max-h-[480px]">
            {dati.rows.length === 0 && <li className="px-4 py-6 text-sm text-slate-400 text-center">Nessuna notifica.</li>}
            {dati.rows.map((n) => (
              <li key={n.id} className={n.letta && chat?.notificaId !== n.id ? "opacity-70" : ""}>
                <button type="button" onClick={() => apri(n)} data-testid={`notifica-${n.id}`}
                  className="w-full text-left px-4 pt-3 pb-1 hover:bg-slate-50 transition-colors duration-150">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm font-semibold text-slate-900">
                      {!n.letta && <span className="inline-block h-2 w-2 rounded-full bg-[#C8102E] mr-2 align-middle" />}
                      {n.tipo === "risposta" && <Reply size={12} className="inline mr-1.5 text-[#0018A8]" />}
                      {n.external_id}
                    </span>
                    <span className="text-[11px] text-slate-400 whitespace-nowrap">{quando(n.creata_il)}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5 truncate">
                    {n.dealer_nome || "—"}{n.importo_finanziato ? ` · ${eur(n.importo_finanziato)}` : ""}{n.cliente ? ` · ${n.cliente}` : ""}
                  </div>
                  {n.tipo === "risposta" ? (
                    <div className="text-xs text-[#0018A8] bg-blue-50 rounded px-2 py-1 mt-1" data-testid="notifica-risposta-testo">
                      <b>{n.da_nome}:</b> {n.nota}
                    </div>
                  ) : (
                    <>
                      {n.nota && <div className="text-xs text-amber-800 bg-amber-50 rounded px-2 py-1 mt-1">{n.nota}</div>}
                      <div className="text-[11px] text-slate-400 mt-1">segnalata da {n.da_nome}</div>
                    </>
                  )}
                </button>
                <div className="px-4 pb-3">
                  <button type="button" onClick={() => apriChat(n)} data-testid={`risposta-btn-${n.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#0018A8] hover:underline">
                    <MessageSquare size={12} /> {chat?.notificaId === n.id ? "Chiudi chat" : "Rispondi · apri chat"}
                  </button>
                  {chat?.notificaId === n.id && (
                    <ChatSegnalazione chatId={chat.chatId} notifica={n} me={user?.email}
                      onClose={() => setChat(null)} onChange={carica} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
  );

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="notifiche-btn" title="Notifiche"
        className="relative h-10 w-10 grid place-items-center rounded-full border border-slate-200 text-slate-600
          hover:border-[#0018A8] hover:text-[#0018A8] transition-colors duration-200">
        <Bell size={18} />
        {dati.non_lette > 0 && (
          <span data-testid="notifiche-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C8102E] text-white
              text-[10px] font-bold grid place-items-center animate-pulse">{dati.non_lette}</span>
        )}
      </button>
      {open && (window.innerWidth < 640 ? createPortal(pannello, document.body) : pannello)}
    </div>
  );
}
